import React, { useEffect, useState } from 'react';

import { WarningMessage } from '@eika/attention-message';
import { Combobox } from '@eika/combobox';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';

import { useReceiverResidenceCountries } from '../../../../fetchHooks/useCountryDetails';
import useTranslation from '../../../../hooks/useTranslation';
import { CrossBorderPaymentActionType } from '../form/actions';
import { MAX_ADDRESS_LENGTH } from '../form/state';
import useFormContext from '../form/useFormContext';
import { checkForNumbersInName, receiverError } from '../form/validate';
import NewCrossBorderReceiverSkeleton from './NewCrossBorderReceiverSkeleton';

import './NewCrossBorderReceiver.scss';

interface Props {
    validationErrors?: receiverError;
    setValidationErrors: (errors: receiverError) => void;
}

const NewCrossBorderReceiverCreditorDetails: React.FC<Props> = ({ validationErrors, setValidationErrors }) => {
    const translated = useTranslation();
    const { state, handleChange } = useFormContext();
    const { data: residenceList, isLoading: loadingCountries } = useReceiverResidenceCountries();
    const [residenceCountry, setResidenceCountry] = useState<string>();
    const showNumbersInNameInfoMessage = checkForNumbersInName(state.receiverName ?? '');

    const handleCountrySearch = (value: string) => {
        setValidationErrors({ receiverCountryCode: undefined });
        const country = residenceList?.find((country) => country.name.toLowerCase() === value.toLowerCase());
        handleChange({
            type: CrossBorderPaymentActionType.SET_RECEIVER_COUNTRY,
            payload: country?.countryCode,
        });
        setResidenceCountry(country?.name ?? value);
    };

    useEffect(() => {
        if (!state.receiver?.receiverCountryCode) return;
        const receiverCountry = residenceList?.find(
            (country) => country.countryCode.value === state.receiver?.receiverCountryCode?.value,
        );
        setResidenceCountry(receiverCountry?.name);
    }, [residenceList, state.receiver?.receiverCountryCode]);

    return (
        <>
            <h3 className="new-receiver-sub-title">{translated.crossBorderPayment.newReceiver.creditorInfo}</h3>
            <Input
                label={translated.crossBorderPayment.newReceiver.receiverNameLabel}
                value={state.receiverName}
                onChange={(value) => {
                    handleChange({
                        type: CrossBorderPaymentActionType.SET_RECEIVER_NAME,
                        payload: value.target.value,
                    });
                    setValidationErrors({ receiverName: undefined });
                }}
                className="cross-border-payment__receiver-name max-desktop-width"
                data-testid="new-cross-border-receiver-name"
                error={validationErrors?.receiverName}
            />
            {showNumbersInNameInfoMessage && (
                <WarningMessage
                    className="cross-border-payment__warning-message max-desktop-width"
                    icon={IconTypes.INFO_24}
                    text={translated.crossBorderPayment.numbersInNameInfoMessage}
                />
            )}
            {state.country?.requiresCreditorDetails && (
                <>
                    <div className="cross-border-payment__field-top">
                        <Input
                            label={translated.crossBorderPayment.newReceiver.receiverAddress}
                            sublabel={translated.crossBorderPayment.newReceiver.addressPlaceholder}
                            className="cross-border-payment__address1"
                            value={state.receiver?.address ?? ''}
                            maxLength={MAX_ADDRESS_LENGTH}
                            onChange={(value) => {
                                handleChange({
                                    type: CrossBorderPaymentActionType.SET_RECEIVER_ADDRESS,
                                    payload: value.target.value,
                                });
                                setValidationErrors({ creditorDetails: undefined });
                            }}
                            error={validationErrors?.creditorDetails}
                            data-testid="receiver-address-input"
                        />
                        <Input
                            value={state.receiver?.address2 ?? ''}
                            maxLength={MAX_ADDRESS_LENGTH}
                            onChange={(value) =>
                                handleChange({
                                    type: CrossBorderPaymentActionType.SET_RECEIVER_ADDRESS_2,
                                    payload: value.target.value,
                                })
                            }
                        />
                    </div>
                    {loadingCountries && <NewCrossBorderReceiverSkeleton />}
                    {!loadingCountries && residenceList && (
                        <div className="cross-border-payment__country-search max-desktop-width">
                            <Combobox
                                id="new-receiver-creditor-details"
                                label={translated.crossBorderPayment.newReceiver.countryLabel}
                                placeholder={translated.crossBorderPayment.newReceiver.countryPlaceholder}
                                suggestions={residenceList?.map((country) => {
                                    return { value: country.name };
                                })}
                                value={residenceCountry ?? ''}
                                onChange={(value) => {
                                    handleCountrySearch(value);
                                }}
                                onSelect={(value) => handleCountrySearch(value.toString())}
                                error={validationErrors?.receiverCountryCode}
                                searchInput
                                hideSearchIcon
                                data-testid="receiver-address-country-input"
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default NewCrossBorderReceiverCreditorDetails;
